import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import DataTable, { TableColumn } from "react-data-table-component";
import { tableCustomStyles } from "../../utils/table-styles";
import * as FileSaver from "file-saver";

import { useAppDispatch, useAppSelector } from "../../hooks";
import { getSortBy, updateSortBy } from "../../store/ticketStore";
import { Ticket } from "../../interface/api/Ticket";
import { toast } from "react-toastify";
import {
  getContractorCompanies,
  setContractorCompanies,
} from "../../store/contractorStore";
import {
  getTransportCompanies,
  setTransportCompanies,
} from "../../store/transportStore";
import { ContractorCompany } from "../../interface/api/ContractorCompany";
import { TransportCompany } from "../../interface/api/TransportCompany";
import contractorCompanyService from "../../services/contractorCompanyService";
import transportCompanyService from "../../services/transportCompanyService";
import PageHero from "../shared/page-hero";
import {
  formatDate,
  formatDateTime,
  formatTime,
} from "../../utils/date-helper";
import Button from "../shared/Button";
import { ReceivedIcon, SoilFLOIcon } from "../shared/icons";
import { ContractorCompaniesWithThresholdsResponse } from "../../interface/api/ContractorCompaniesWithThresholdsResponse";
import GenerateReportForm from "./forms/generate-report-form";
import { GenerateReportFormValues } from "../../interface/formik/GenerateReportFormValues";
import ticketService from "../../services/ticketService";
import smallQuantityTicketService from "../../services/smallQuantityTicketService";
import hydrovacTicketService from "../../services/hydrovacTicketService";
import rubbleTicketService from "../../services/rubbleTicketService";
import { hydrovacTicketNumber } from "../../utils/parser";
import { a11yProps } from "../../utils/constants";
import { TabPanel } from "../shared/tab-panel";
import TemplateReportForm from "./forms/template-report-form";
import RoyaltyReportView from "./royalty/royalty-report-view";
import PaginatedTableTop, { paginationTopClassName } from "../shared/paginated-table-top";
import { ReportStream } from "../../interface/forms/GenerateReportFormProps";
import { orderBy as sort } from "lodash";

// @ts-ignore
import * as XLSX from "sheetjs-style";

// --- Column ID enums per stream ---

const MainColumnIds = {
  Received: 1,
  TicketNumber: 2,
  JobSite: 3,
  ProjectId: 4,
  ContractorCompany: 5,
  SiteContactName: 6,
  SiteContactNumber: 7,
  OfficeContact: 8,
  OfficeContactNumber: 9,
  OfficeContactEmail: 10,
  Date: 11,
  TimeLoaded: 12,
  SoilFLOLoad: 13,
  ProjectLeader: 14,
  HaulageCo: 15,
  TransportContact: 16,
  TransportContactNumber: 17,
  TruckType: 18,
  MaterialTypeName: 19,
  Material: 20,
  Weight: 21,
  TruckDriver: 22,
  TruckDriverNumber: 23,
  TruckLicensePlate: 24,
  TruckNumber: 25,
  ContactName: 26,
  TimeReceived: 27,
  Notes: 28,
  SoilFLOTicketId: 29,
};

const SmallQtyColumnIds = {
  Received: 1,
  TicketNumber: 2,
  Contractor: 3,
  JobSite: 4,
  Date: 5,
  TimeLoaded: 6,
  TimeReceived: 7,
  TruckNumber: 8,
  LicensePlate: 9,
  HaulageCompany: 10,
  ContactName: 11,
  PaymentMethod: 12,
  SalePrice: 13,
  Stockpile: 14,
  StockpileStatus: 15,
  StockpileSequence: 16,
  Sampled: 17,
  Notes: 18,
  DriverName: 19,
};

const HydrovacColumnIds = {
  Received: 1,
  TicketNumber: 2,
  Contractor: 3,
  PropertyAddress: 4,
  LandUse: 5,
  Date: 6,
  TimeLoaded: 7,
  TimeUnloaded: 8,
  ShippingCompany: 9,
  ShippingDriver: 10,
  HeadspaceReading: 11,
  Status: 12,
  SalePrice: 13,
  ContainmentPad: 14,
  ContainmentPadStatus: 15,
  ContainmentPadSequence: 16,
  PadSampleReference: 17,
  Notes: 18,
  LicensePlate: 19,
  TruckNumber: 20,
};

const RubbleColumnIds = {
  Received: 1,
  TicketNumber: 2,
  Contractor: 3,
  JobSite: 4,
  Date: 5,
  TimeLoaded: 6,
  TimeReceived: 7,
  TruckNumber: 8,
  LicensePlate: 9,
  HaulageCompany: 10,
  ContactName: 11,
  PaymentMethod: 12,
  SalePrice: 13,
  PurchasePrice: 14,
  Notes: 15,
  DriverName: 16,
};

// --- Column definitions per stream ---

const mainColumns: TableColumn<any>[] = [
  {
    id: MainColumnIds.Received,
    name: "Received",
    cell: (row: any) => <span>{row.isReceived && <ReceivedIcon />}</span>,
  },
  {
    id: MainColumnIds.TicketNumber,
    name: "Ticket #",
    selector: (row: any) => row.ticketNumber,
    sortable: true,
  },
  {
    id: MainColumnIds.JobSite,
    name: "Job Site",
    selector: (row: any) => row.jobSite,
    sortable: true,
  },
  {
    id: MainColumnIds.ProjectId,
    name: "Project ID",
    selector: (row: any) => row.projectProjectId,
    sortable: true,
  },
  {
    id: MainColumnIds.ContractorCompany,
    name: "Contractor Company",
    selector: (row: any) => row.contractorCompanyName,
    sortable: true,
  },
  {
    id: MainColumnIds.SiteContactName,
    name: "Site Contact Name",
    selector: (row: any) => row.siteContactName,
    sortable: true,
  },
  {
    id: MainColumnIds.SiteContactNumber,
    name: "Site Contact Number",
    selector: (row: any) => row.siteContactNumber,
    sortable: true,
  },
  {
    id: MainColumnIds.OfficeContact,
    name: "Office Contact",
    selector: (row: any) => row.contractorOfficeContactName,
    sortable: true,
  },
  {
    id: MainColumnIds.OfficeContactNumber,
    name: "Office Contact Number",
    selector: (row: any) => row.contractorOfficeContactNumber,
    sortable: true,
  },
  {
    id: MainColumnIds.OfficeContactEmail,
    name: "Office Contact Email",
    selector: (row: any) => row.contractorOfficeContactEmail,
    sortable: true,
  },
  {
    id: MainColumnIds.Date,
    name: "Date",
    selector: (row: any) =>
      row.timeLoaded ? new Date(row.timeLoaded).toLocaleDateString() : "",
    sortable: true,
  },
  {
    id: MainColumnIds.TimeLoaded,
    name: "Time Loaded",
    selector: (row: any) => formatTime(row.timeLoaded, row.isSoilFLOLoad),
    sortable: true,
  },
  {
    id: MainColumnIds.SoilFLOLoad,
    name: "SoilFLO Load",
    cell: (row: any) => <span>{row.isSoilFLOLoad && <SoilFLOIcon />}</span>,
  },
  {
    id: MainColumnIds.ProjectLeader,
    name: "Project Leader",
    selector: (row: any) => row.projectLeader,
    sortable: true,
  },
  {
    id: MainColumnIds.HaulageCo,
    name: "Haulage Co.",
    selector: (row: any) => row.transportCompanyName,
    sortable: true,
  },
  {
    id: MainColumnIds.TransportContact,
    name: "Transport Contact",
    selector: (row: any) => row.transportContactName,
    sortable: true,
  },
  {
    id: MainColumnIds.TransportContactNumber,
    name: "Transport Contact Number",
    selector: (row: any) => row.transportContactNumber,
    sortable: true,
  },
  {
    id: MainColumnIds.TruckType,
    name: "Truck Type",
    selector: (row: any) => row.truckTypeName,
    sortable: true,
  },
  {
    id: MainColumnIds.MaterialTypeName,
    name: "Material Type Name",
    selector: (row: any) => (row.materialTypeName ? row.materialTypeName : ""),
    sortable: true,
  },
  {
    id: MainColumnIds.Material,
    name: "Material",
    selector: (row: any) => (row.materialName ? row.materialName : ""),
    sortable: true,
  },
  {
    id: MainColumnIds.Weight,
    name: "Weight",
    selector: (row: any) => (row.weight ? row.weight : ""),
    sortable: true,
  },
  {
    id: MainColumnIds.TruckDriver,
    name: "Truck Driver",
    selector: (row: any) => row.truckDriverName,
    sortable: true,
  },
  {
    id: MainColumnIds.TruckDriverNumber,
    name: "Truck Driver Number",
    selector: (row: any) => row.truckDriverNumber,
    sortable: true,
  },
  {
    id: MainColumnIds.TruckLicensePlate,
    name: "Truck License Plate",
    selector: (row: any) => row.truckLicensePlate,
    sortable: true,
  },
  {
    id: MainColumnIds.TruckNumber,
    name: "Truck #",
    selector: (row: any) =>
      Number(row.truckNumber) > 0 ? row.truckNumber : "",
    sortable: true,
  },
  {
    id: MainColumnIds.ContactName,
    name: "Contact Name",
    selector: (row: any) =>
      row.userFirstName && row.userLastName
        ? `${row.userFirstName} ${row.userLastName}`
        : "",
    sortable: true,
  },
  {
    id: MainColumnIds.TimeReceived,
    name: "Time Received",
    selector: (row: any) => formatTime(row.timeReceived, false),
    sortable: true,
  },
  {
    id: MainColumnIds.Notes,
    name: "Notes",
    selector: (row: any) => row.notes,
    sortable: false,
  },
  {
    id: MainColumnIds.SoilFLOTicketId,
    name: "SoilFLOTicketId",
    selector: (row: any) => (row.soilFLOTicketId ? row.soilFLOTicketId : ""),
    sortable: true,
  },
];

const smallQtyColumns: TableColumn<any>[] = [
  {
    id: SmallQtyColumnIds.Received,
    name: "Received",
    cell: (row: any) => <span>{row.isReceived && <ReceivedIcon />}</span>,
  },
  {
    id: SmallQtyColumnIds.TicketNumber,
    name: "Ticket #",
    selector: (row: any) => row.ticketNumber,
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.Contractor,
    name: "Contractor",
    selector: (row: any) => row.contractorCompanyName ?? "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.JobSite,
    name: "Job Site",
    selector: (row: any) => row.jobSite ?? "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.Date,
    name: "Date",
    selector: (row: any) =>
      row.date ? new Date(row.date).toLocaleDateString() : "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.TimeLoaded,
    name: "Time Loaded",
    selector: (row: any) => formatTime(row.timeLoaded, false),
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.TimeReceived,
    name: "Time Received",
    selector: (row: any) => formatTime(row.timeReceived, false),
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.TruckNumber,
    name: "Truck #",
    selector: (row: any) => row.truckNumber ?? "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.LicensePlate,
    name: "License Plate",
    selector: (row: any) => row.truckLicensePlate ?? "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.DriverName,
    name: "Driver Name",
    selector: (row: any) => row.truckDriverName ?? "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.HaulageCompany,
    name: "Haulage Company",
    selector: (row: any) => row.haulageCompanyName ?? "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.ContactName,
    name: "Contact Name",
    selector: (row: any) =>
      row.userFirstName && row.userLastName
        ? `${row.userFirstName} ${row.userLastName}`
        : "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.PaymentMethod,
    name: "Payment Method",
    selector: (row: any) => row.paymentMethod ?? "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.SalePrice,
    name: "Sale Price",
    selector: (row: any) => (row.salePrice != null ? row.salePrice : ""),
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.Stockpile,
    name: "Stockpile",
    selector: (row: any) => row.stockpileName ?? "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.StockpileStatus,
    name: "Stockpile Status",
    selector: (row: any) => row.stockpileStatus ?? "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.StockpileSequence,
    name: "Stockpile Seq #",
    selector: (row: any) =>
      row.stockpileSequenceNumber != null ? row.stockpileSequenceNumber : "",
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.Sampled,
    name: "Sampled",
    selector: (row: any) => (row.isSampled ? "Yes" : ""),
    sortable: true,
  },
  {
    id: SmallQtyColumnIds.Notes,
    name: "Notes",
    selector: (row: any) => row.notes ?? "",
    sortable: false,
  },
];

const hydrovacColumns: TableColumn<any>[] = [
  {
    id: HydrovacColumnIds.Received,
    name: "Received",
    cell: (row: any) => <span>{row.isReceived && <ReceivedIcon />}</span>,
  },
  {
    id: HydrovacColumnIds.TicketNumber,
    name: "Ticket #",
    selector: (row: any) => row.ticketNumber,
    cell: (row: any) => hydrovacTicketNumber(row),
    sortable: true,
  },
  {
    id: HydrovacColumnIds.Contractor,
    name: "Contractor",
    selector: (row: any) => row.contractorCompanyName ?? "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.PropertyAddress,
    name: "Property Address",
    selector: (row: any) => row.propertyAddress ?? "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.LandUse,
    name: "Land Use",
    selector: (row: any) => row.landUse ?? "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.Date,
    name: "Date",
    selector: (row: any) =>
      row.date ? new Date(row.date).toLocaleDateString() : "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.TimeLoaded,
    name: "Time Loaded",
    selector: (row: any) => formatTime(row.timeLoaded, false),
    sortable: true,
  },
  {
    id: HydrovacColumnIds.TimeUnloaded,
    name: "Time Unloaded",
    selector: (row: any) => formatTime(row.timeUnloaded, false),
    sortable: true,
  },
  {
    id: HydrovacColumnIds.ShippingCompany,
    name: "Shipping Company",
    selector: (row: any) => row.shippingCompany ?? "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.ShippingDriver,
    name: "Shipping Driver",
    selector: (row: any) => row.shippingDriver ?? "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.TruckNumber,
    name: "Truck #",
    selector: (row: any) => row.truckNumber ?? "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.LicensePlate,
    name: "License Plate",
    selector: (row: any) => row.truckLicensePlate ?? "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.HeadspaceReading,
    name: "Headspace Reading",
    selector: (row: any) =>
      row.headspaceReading != null ? row.headspaceReading : "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.Status,
    name: "Status",
    selector: (row: any) => {
      if (row.isComplete) return "Complete";
      if (row.isPendingHeadspace) return "Pending Headspace";
      if (row.isReceived) return "Received";
      return "";
    },
    sortable: true,
  },
  {
    id: HydrovacColumnIds.SalePrice,
    name: "Sale Price",
    selector: (row: any) => (row.salePrice != null ? row.salePrice : ""),
    sortable: true,
  },
  {
    id: HydrovacColumnIds.ContainmentPad,
    name: "Containment Pad",
    selector: (row: any) => row.containmentPadName ?? "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.ContainmentPadStatus,
    name: "Pad Status",
    selector: (row: any) => row.containmentPadStatus ?? "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.ContainmentPadSequence,
    name: "Pad Seq #",
    selector: (row: any) =>
      row.containmentPadSequenceNumber != null
        ? row.containmentPadSequenceNumber
        : "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.PadSampleReference,
    name: "Pad Sample Ref #",
    selector: (row: any) => row.padSampleReferenceNumber ?? "",
    sortable: true,
  },
  {
    id: HydrovacColumnIds.Notes,
    name: "Notes",
    selector: (row: any) => row.notes ?? "",
    sortable: false,
  },
];

const rubbleColumns: TableColumn<any>[] = [
  {
    id: RubbleColumnIds.Received,
    name: "Received",
    cell: (row: any) => <span>{row.isReceived && <ReceivedIcon />}</span>,
  },
  {
    id: RubbleColumnIds.TicketNumber,
    name: "Ticket #",
    selector: (row: any) => row.ticketNumber,
    sortable: true,
  },
  {
    id: RubbleColumnIds.Contractor,
    name: "Contractor",
    selector: (row: any) => row.contractorCompanyName ?? "",
    sortable: true,
  },
  {
    id: RubbleColumnIds.JobSite,
    name: "Job Site",
    selector: (row: any) => row.jobSite ?? "",
    sortable: true,
  },
  {
    id: RubbleColumnIds.Date,
    name: "Date",
    selector: (row: any) =>
      row.date ? new Date(row.date).toLocaleDateString() : "",
    sortable: true,
  },
  {
    id: RubbleColumnIds.TimeLoaded,
    name: "Time Loaded",
    selector: (row: any) => formatTime(row.timeLoaded, false),
    sortable: true,
  },
  {
    id: RubbleColumnIds.TimeReceived,
    name: "Time Received",
    selector: (row: any) => formatTime(row.timeReceived, false),
    sortable: true,
  },
  {
    id: RubbleColumnIds.TruckNumber,
    name: "Truck #",
    selector: (row: any) => row.truckNumber ?? "",
    sortable: true,
  },
  {
    id: RubbleColumnIds.LicensePlate,
    name: "License Plate",
    selector: (row: any) => row.truckLicensePlate ?? "",
    sortable: true,
  },
  {
    id: RubbleColumnIds.DriverName,
    name: "Driver Name",
    selector: (row: any) => row.truckDriverName ?? "",
    sortable: true,
  },
  {
    id: RubbleColumnIds.HaulageCompany,
    name: "Haulage Company",
    selector: (row: any) => row.haulageCompanyName ?? "",
    sortable: true,
  },
  {
    id: RubbleColumnIds.ContactName,
    name: "Contact Name",
    selector: (row: any) =>
      row.userFirstName && row.userLastName
        ? `${row.userFirstName} ${row.userLastName}`
        : "",
    sortable: true,
  },
  {
    id: RubbleColumnIds.PaymentMethod,
    name: "Payment Method",
    selector: (row: any) => row.paymentMethod ?? "",
    sortable: true,
  },
  {
    id: RubbleColumnIds.SalePrice,
    name: "Sale Price",
    selector: (row: any) => (row.salePrice != null ? row.salePrice : ""),
    sortable: true,
  },
  {
    id: RubbleColumnIds.PurchasePrice,
    name: "Purchase Price",
    selector: (row: any) => (row.purchasePrice != null ? row.purchasePrice : ""),
    sortable: true,
  },
  {
    id: RubbleColumnIds.Notes,
    name: "Notes",
    selector: (row: any) => row.notes ?? "",
    sortable: false,
  },
];

// --- Export value mappers ---

const getMainExportValue = (ticket: any, key: number): any => {
  switch (key) {
    case MainColumnIds.Received:
      return ticket.isReceived ? "yes" : "no";
    case MainColumnIds.TicketNumber:
      return ticket.ticketNumber;
    case MainColumnIds.JobSite:
      return ticket.jobSite;
    case MainColumnIds.ProjectId:
      return ticket.projectProjectId;
    case MainColumnIds.ContractorCompany:
      return ticket.contractorCompanyName;
    case MainColumnIds.SiteContactName:
      return ticket.siteContactName;
    case MainColumnIds.SiteContactNumber:
      return ticket.siteContactNumber;
    case MainColumnIds.OfficeContact:
      return ticket.contractorOfficeContactName;
    case MainColumnIds.OfficeContactNumber:
      return ticket.contractorOfficeContactNumber;
    case MainColumnIds.OfficeContactEmail:
      return ticket.contractorOfficeContactEmail;
    case MainColumnIds.Date:
      return formatDate(ticket.timeReceived, false);
    case MainColumnIds.TimeLoaded:
      return formatDateTime(ticket.timeLoaded, ticket.isSoilFLOLoad);
    case MainColumnIds.SoilFLOLoad:
      return ticket.isSoilFLOLoad ? "yes" : "no";
    case MainColumnIds.ProjectLeader:
      return ticket.projectLeader;
    case MainColumnIds.HaulageCo:
      return ticket.transportCompanyName;
    case MainColumnIds.TransportContact:
      return ticket.transportContactName;
    case MainColumnIds.TransportContactNumber:
      return ticket.transportContactNumber;
    case MainColumnIds.TruckType:
      return ticket.truckTypeName;
    case MainColumnIds.MaterialTypeName:
      return ticket.materialTypeName;
    case MainColumnIds.Material:
      return ticket.materialName;
    case MainColumnIds.Weight:
      return ticket.weight;
    case MainColumnIds.TruckDriver:
      return ticket.truckDriverName;
    case MainColumnIds.TruckDriverNumber:
      return ticket.truckDriverNumber;
    case MainColumnIds.TruckLicensePlate:
      return ticket.truckLicensePlate;
    case MainColumnIds.TruckNumber:
      return ticket.truckNumber;
    case MainColumnIds.ContactName:
      return `${ticket.userFirstName ?? ""} ${ticket.userLastName ?? ""}`.trim();
    case MainColumnIds.TimeReceived:
      return formatDateTime(ticket.timeReceived, false);
    case MainColumnIds.Notes:
      return ticket.notes;
    case MainColumnIds.SoilFLOTicketId:
      return ticket.soilFLOTicketId;
    default:
      return "";
  }
};

const getSmallQtyExportValue = (ticket: any, key: number): any => {
  switch (key) {
    case SmallQtyColumnIds.Received:
      return ticket.isReceived ? "yes" : "no";
    case SmallQtyColumnIds.TicketNumber:
      return ticket.ticketNumber;
    case SmallQtyColumnIds.Contractor:
      return ticket.contractorCompanyName ?? "";
    case SmallQtyColumnIds.JobSite:
      return ticket.jobSite ?? "";
    case SmallQtyColumnIds.Date:
      return formatDate(ticket.date, false);
    case SmallQtyColumnIds.TimeLoaded:
      return formatDateTime(ticket.timeLoaded, false);
    case SmallQtyColumnIds.TimeReceived:
      return formatDateTime(ticket.timeReceived, false);
    case SmallQtyColumnIds.TruckNumber:
      return ticket.truckNumber ?? "";
    case SmallQtyColumnIds.LicensePlate:
      return ticket.truckLicensePlate ?? "";
    case SmallQtyColumnIds.DriverName:
      return ticket.truckDriverName ?? "";
    case SmallQtyColumnIds.HaulageCompany:
      return ticket.haulageCompanyName ?? "";
    case SmallQtyColumnIds.ContactName:
      return `${ticket.userFirstName ?? ""} ${ticket.userLastName ?? ""}`.trim();
    case SmallQtyColumnIds.PaymentMethod:
      return ticket.paymentMethod ?? "";
    case SmallQtyColumnIds.SalePrice:
      return ticket.salePrice;
    case SmallQtyColumnIds.Stockpile:
      return ticket.stockpileName ?? "";
    case SmallQtyColumnIds.StockpileStatus:
      return ticket.stockpileStatus ?? "";
    case SmallQtyColumnIds.StockpileSequence:
      return ticket.stockpileSequenceNumber != null
        ? ticket.stockpileSequenceNumber
        : "";
    case SmallQtyColumnIds.Sampled:
      return ticket.isSampled ? "yes" : "no";
    case SmallQtyColumnIds.Notes:
      return ticket.notes ?? "";
    default:
      return "";
  }
};

const getHydrovacExportValue = (ticket: any, key: number): any => {
  switch (key) {
    case HydrovacColumnIds.Received:
      return ticket.isReceived ? "yes" : "no";
    case HydrovacColumnIds.TicketNumber:
      return hydrovacTicketNumber(ticket);
    case HydrovacColumnIds.Contractor:
      return ticket.contractorCompanyName ?? "";
    case HydrovacColumnIds.PropertyAddress:
      return ticket.propertyAddress ?? "";
    case HydrovacColumnIds.LandUse:
      return ticket.landUse ?? "";
    case HydrovacColumnIds.Date:
      return formatDate(ticket.date, false);
    case HydrovacColumnIds.TimeLoaded:
      return formatDateTime(ticket.timeLoaded, false);
    case HydrovacColumnIds.TimeUnloaded:
      return formatDateTime(ticket.timeUnloaded, false);
    case HydrovacColumnIds.ShippingCompany:
      return ticket.shippingCompany ?? "";
    case HydrovacColumnIds.ShippingDriver:
      return ticket.shippingDriver ?? "";
    case HydrovacColumnIds.TruckNumber:
      return ticket.truckNumber ?? "";
    case HydrovacColumnIds.LicensePlate:
      return ticket.truckLicensePlate ?? "";
    case HydrovacColumnIds.HeadspaceReading:
      return ticket.headspaceReading;
    case HydrovacColumnIds.Status:
      if (ticket.isComplete) return "Complete";
      if (ticket.isPendingHeadspace) return "Pending Headspace";
      if (ticket.isReceived) return "Received";
      return "";
    case HydrovacColumnIds.SalePrice:
      return ticket.salePrice;
    case HydrovacColumnIds.ContainmentPad:
      return ticket.containmentPadName ?? "";
    case HydrovacColumnIds.ContainmentPadStatus:
      return ticket.containmentPadStatus ?? "";
    case HydrovacColumnIds.ContainmentPadSequence:
      return ticket.containmentPadSequenceNumber != null
        ? ticket.containmentPadSequenceNumber
        : "";
    case HydrovacColumnIds.PadSampleReference:
      return ticket.padSampleReferenceNumber ?? "";
    case HydrovacColumnIds.Notes:
      return ticket.notes ?? "";
    default:
      return "";
  }
};

const getRubbleExportValue = (ticket: any, key: number): any => {
  switch (key) {
    case RubbleColumnIds.Received:
      return ticket.isReceived ? "yes" : "no";
    case RubbleColumnIds.TicketNumber:
      return ticket.ticketNumber;
    case RubbleColumnIds.Contractor:
      return ticket.contractorCompanyName ?? "";
    case RubbleColumnIds.JobSite:
      return ticket.jobSite ?? "";
    case RubbleColumnIds.Date:
      return formatDate(ticket.date, false);
    case RubbleColumnIds.TimeLoaded:
      return formatDateTime(ticket.timeLoaded, false);
    case RubbleColumnIds.TimeReceived:
      return formatDateTime(ticket.timeReceived, false);
    case RubbleColumnIds.TruckNumber:
      return ticket.truckNumber ?? "";
    case RubbleColumnIds.LicensePlate:
      return ticket.truckLicensePlate ?? "";
    case RubbleColumnIds.DriverName:
      return ticket.truckDriverName ?? "";
    case RubbleColumnIds.HaulageCompany:
      return ticket.haulageCompanyName ?? "";
    case RubbleColumnIds.ContactName:
      return `${ticket.userFirstName ?? ""} ${ticket.userLastName ?? ""}`.trim();
    case RubbleColumnIds.PaymentMethod:
      return ticket.paymentMethod ?? "";
    case RubbleColumnIds.SalePrice:
      return ticket.salePrice;
    case RubbleColumnIds.PurchasePrice:
      return ticket.purchasePrice;
    case RubbleColumnIds.Notes:
      return ticket.notes ?? "";
    default:
      return "";
  }
};

// --- Helper ---

const getColumnsForStream = (stream: ReportStream): TableColumn<any>[] => {
  switch (stream) {
    case "smallQuantity":
      return smallQtyColumns;
    case "hydrovac":
      return hydrovacColumns;
    case "rubble":
      return rubbleColumns;
    default:
      return mainColumns;
  }
};

const getExportMapper = (
  stream: ReportStream
): ((ticket: any, key: number) => any) => {
  switch (stream) {
    case "smallQuantity":
      return getSmallQtyExportValue;
    case "hydrovac":
      return getHydrovacExportValue;
    case "rubble":
      return getRubbleExportValue;
    default:
      return getMainExportValue;
  }
};

const streamColors: Record<ReportStream, { bg: string; color: string }> = {
  main: { bg: "#032e5b", color: "#fff" },
  smallQuantity: { bg: "#90caf9", color: "#0d47a1" },
  hydrovac: { bg: "#e0e0e0", color: "#424242" },
  rubble: { bg: "#FFE0B2", color: "#e65100" },
};

const streamLabels: { value: ReportStream; label: string }[] = [
  { value: "main", label: "Main" },
  { value: "smallQuantity", label: "Small Quantity" },
  { value: "rubble", label: "Rubble" },
  { value: "hydrovac", label: "Hydrovac" },
];

// =======================================================

const ReportsView = () => {
  enum TabValues {
    Custom,
    Template,
    Royalty,
  }

  const dispatch = useAppDispatch();
  const contractorCompanies = useAppSelector(getContractorCompanies);
  const transportCompanies = useAppSelector(getTransportCompanies);
  const sortBy = useAppSelector(getSortBy);
  const [value, setValue] = useState(TabValues.Custom);
  const [reportTitle, setReportTitle] = useState("Ticket Summary Report");
  const [stream, setStream] = useState<ReportStream>("main");
  // Hydrovac only: which ticket batch to report on. "all" = both, "standard" =
  // regular (numeric) only, "b" = "Tractor Trailer size" B-series only.
  const [hydrovacBatch, setHydrovacBatch] = useState<"all" | "standard" | "b">(
    "all"
  );
  const [tickets, setTickets] = useState<any[]>([]);

  const generateReportFormValues: GenerateReportFormValues = {
    transportCompanyId: 0,
    contractorCompanyId: 0,
    jobSite: "",
    fromDate: null,
    toDate: null,
    truckLicensePlate: "",
    from: "",
    to: "",
    projectId: "",
  };

  const [reportValues, setReportValues] = useState<GenerateReportFormValues>(
    generateReportFormValues
  );

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const contractorResponse =
          await contractorCompanyService.getContractorCompanies();
        if (contractorResponse.ok) {
          const contractorCompanies = (
            contractorResponse.data as ContractorCompaniesWithThresholdsResponse
          ).contractorCompanies as ContractorCompany[];

          dispatch(setContractorCompanies({ contractorCompanies }));
        } else {
          toast.error(contractorResponse.originalError.message);
        }

        const transportResponse =
          await transportCompanyService.getTransportCompanies();
        if (transportResponse.ok) {
          dispatch(
            setTransportCompanies({
              transportCompanies: transportResponse.data as TransportCompany[],
            })
          );
        } else {
          toast.error(transportResponse.originalError.message);
        }
      } catch (error) {
        console.error("Failed to fetch companies:", error);
        toast.error("Error loading contractor or transport companies.");
      }
    };

    fetchCompanies();
  }, [dispatch]);

  const columns = useMemo(() => getColumnsForStream(stream), [stream]);

  const sortedTickets = useMemo(() => {
    if (sortBy === "ticketNumber") {
      return sort(tickets, [sortBy], ["desc"]);
    }
    if (sortBy === "contractorCompanyName" || sortBy === "shippingCompany") {
      return sort(tickets, [sortBy], ["asc"]);
    }
    return tickets;
  }, [tickets, sortBy]);

  const [visibleColumnIds, setVisibleColumnIds] = useState<
    (string | number | undefined)[]
  >(mainColumns.slice(0, 6).map((col) => col.id));

  // Reset tickets and visible columns when stream changes
  useEffect(() => {
    const cols = getColumnsForStream(stream);
    if (stream === "main") {
      setVisibleColumnIds(cols.slice(0, 6).map((col) => col.id));
    } else {
      setVisibleColumnIds(cols.map((col) => col.id));
    }
    setHydrovacBatch("all");
    setTickets([]);
  }, [stream]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => visibleColumnIds.includes(col.id)),
    [columns, visibleColumnIds]
  );

  const onSubmit = (
    values: GenerateReportFormValues,
    setSubmitting: Function
  ) => {
    let serviceCall;
    switch (stream) {
      case "smallQuantity":
        serviceCall = smallQuantityTicketService.getReportingTickets(values);
        break;
      case "hydrovac":
        serviceCall = hydrovacTicketService.getReportingTickets({
          ...values,
          // null = don't filter (show both batches).
          isBSeries: hydrovacBatch === "all" ? null : hydrovacBatch === "b",
        });
        break;
      case "rubble":
        serviceCall = rubbleTicketService.getReportingTickets(values);
        break;
      default:
        serviceCall = ticketService.getReportingTickets(values);
        break;
    }

    serviceCall
      .then((response) => {
        if (response.ok) {
          setTickets(response.data as any[]);
          setReportValues(values);
        } else {
          toast.error(response.originalError.message);
        }
      })
      .finally(() => setSubmitting(false));
  };

  const onToggleColumnVisible = (id: number) => {
    setVisibleColumnIds((prev) =>
      prev.includes(id) ? prev.filter((colId) => colId !== id) : [...prev, id]
    );
  };

  const columnIdToName = (id: number, columns: TableColumn<any>[]): string => {
    const column = columns.find((col) => col.id === id);
    return column?.name?.toString() ?? `Column ${id}`;
  };

  const onExport = () => {
    try {
      if (!sortedTickets || sortedTickets.length === 0) return;

      const title = reportTitle || "Ticket Summary Report";
      const visibleKeys = visibleColumns.map((col) => col.id);
      const columnTitles = visibleKeys.map((key) =>
        columnIdToName(Number(key), columns)
      );

      const exportMapper = getExportMapper(stream);
      const dataRows = sortedTickets.map((ticket: any) => {
        return visibleKeys.map((key) => exportMapper(ticket, Number(key)));
      });

      const contractorCompany = contractorCompanies.find(
        (cc) => cc.id === reportValues.contractorCompanyId
      );
      const transportCompany = transportCompanies.find(
        (t) => t.id === reportValues.transportCompanyId
      );

      const metaBlock: any[][] = [
        ["UNITED SOILS MANAGEMENT LTD"],
        [title],
        [],
        ["Date", formatDate(new Date(), false), "Ticket Count", sortedTickets.length],
        ["From Ticket", reportValues.from, "To Ticket", reportValues.to],
        [
          "From Date",
          formatDate(reportValues.fromDate, false),
          "To Date",
          formatDate(reportValues.toDate, false),
        ],
        [
          "Contractor",
          contractorCompany?.name ?? "",
          ...(stream === "main"
            ? ["Haulage Co", transportCompany?.name ?? ""]
            : []),
        ],
      ];

      if (stream === "main") {
        metaBlock.push([
          "Job Site",
          reportValues.jobSite,
          "Project ID",
          reportValues.projectId,
        ]);
      }

      const fullData = [...metaBlock, [], columnTitles, ...dataRows];

      const ws = XLSX.utils.aoa_to_sheet(fullData);
      const totalColumns = columnTitles.length;

      // Merge top header rows
      ws["!merges"] = [0, 1, 2].map((r: number) => ({
        s: { r, c: 0 },
        e: { r, c: totalColumns - 1 },
      }));

      // Style merged header cells
      [0, 1, 2].forEach((r: number) => {
        const cellRef = XLSX.utils.encode_cell({ r, c: 0 });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            alignment: { horizontal: "center", vertical: "center" },
            font: { bold: true, sz: 16 },
          };
        }
      });

      const fullBorder = {
        top: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
      };

      const lightGrayFill = {
        patternType: "solid",
        fgColor: { rgb: "F2F2F2" },
      };

      // Style metadata block (rows 3 through end of metaBlock-1)
      const metaDataRows: number[][] = [];
      for (let r = 3; r < metaBlock.length; r++) {
        metaDataRows.push([r, 0, 1]);
        if (metaBlock[r].length > 2) {
          metaDataRows.push([r, 2, 3]);
        }
      }

      metaDataRows.forEach(([r, labelCol, valueCol]) => {
        const labelRef = XLSX.utils.encode_cell({ r, c: labelCol });
        const valueRef = XLSX.utils.encode_cell({ r, c: valueCol });
        if (!ws[labelRef]) ws[labelRef] = { t: "s", v: "" };
        if (!ws[valueRef]) ws[valueRef] = { t: "s", v: "" };
        ws[labelRef].s = {
          font: { bold: true },
          alignment: { horizontal: "left" },
          border: fullBorder,
        };
        ws[valueRef].s = {
          alignment: { horizontal: "left" },
          border: fullBorder,
          fill: lightGrayFill,
        };
      });

      // Column headers row index = metaBlock.length + 1 (after the empty row)
      const headerRowIndex = metaBlock.length + 1;

      // Style column headers
      columnTitles.forEach((_: string, c: number) => {
        const ref = XLSX.utils.encode_cell({ r: headerRowIndex, c });
        if (ws[ref]) {
          ws[ref].s = {
            font: { bold: true, sz: 14 },
            alignment: { horizontal: "center" },
            border: fullBorder,
          };
        }
      });

      // Zebra striping for data rows
      const dataStartRow = headerRowIndex + 1;
      const dataEndRow = dataStartRow + dataRows.length - 1;

      dataRows.forEach((_: any, rowIndex: number) => {
        const r = dataStartRow + rowIndex;
        const isEven = rowIndex % 2 === 0;
        const bgColor = isEven ? "FFFFFF" : "F2F2F2";
        for (let c = 0; c < totalColumns; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (!ws[ref]) ws[ref] = { t: "s", v: "" };
          if (!ws[ref].s) ws[ref].s = {};
          ws[ref].s.fill = {
            patternType: "solid",
            fgColor: { rgb: bgColor },
          };
        }
      });

      // Apply outer border to entire data grid
      for (let r = dataStartRow; r <= dataEndRow; r++) {
        for (let c = 0; c < totalColumns; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (!ws[ref]) ws[ref] = { t: "s", v: "" };
          if (!ws[ref].s) ws[ref].s = {};
          const style = ws[ref].s;
          if (!style.border) style.border = {};
          if (r === dataStartRow) style.border.top = fullBorder.top;
          if (r === dataEndRow) style.border.bottom = fullBorder.bottom;
          if (c === 0) style.border.left = fullBorder.left;
          if (c === totalColumns - 1) style.border.right = fullBorder.right;
        }
      }

      // Auto column widths
      const maxCols = Math.max(...fullData.map((row) => row.length));
      ws["!cols"] = Array.from({ length: maxCols }).map((_, c) => {
        const maxLength = fullData.reduce((max, row) => {
          const val = row[c];
          return Math.max(max, val ? String(val).length : 0);
        }, 0);

        const minWidth = c === 2 ? 14 : 0;
        return { wch: Math.max(maxLength + 2, minWidth) };
      });

      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const buffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      });

      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const fileExtension = ".xlsx";
      const blob = new Blob([buffer], { type: fileType });
      FileSaver.saveAs(blob, `${title}${fileExtension}`);
    } catch (e) {
      console.error("Export error:", e);
    }
  };

  const onSortByChange = (value: "ticketNumber" | "contractorCompanyName" | "shippingCompany") => {
    dispatch(updateSortBy(value));
  };

  const onTabClick = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  const onStreamChange = (newStream: ReportStream) => {
    if (newStream !== stream) {
      setStream(newStream);
    }
  };

  return (
    <Container maxWidth="xl">
      <PageHero
        title="Reports"
        subtitle="Manage Reports."
        showBackButton={false}
      />

      <Tabs
        value={value}
        onChange={onTabClick}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="on"
        aria-label="scrollable tabs"
      >
        <Tab label="Custom" {...a11yProps(0)} />
        <Tab label="Template" {...a11yProps(1)} />
        <Tab label="Royalty" {...a11yProps(2)} />
      </Tabs>

      <TabPanel value={value} index={TabValues.Custom}>
        <Box style={{ marginTop: 64, marginBottom: 64 }}>
          {/* Stream Selector */}
          <Box style={{ marginBottom: 24 }}>
            <Grid container spacing={1} alignItems="center">
              {streamLabels.map((s) => {
                const colors = streamColors[s.value];
                const isActive = stream === s.value;
                return (
                  <Grid item key={s.value}>
                    <Chip
                      label={s.label}
                      onClick={() => onStreamChange(s.value)}
                      clickable
                      style={
                        isActive
                          ? { backgroundColor: colors.bg, color: colors.color, fontWeight: 600 }
                          : { backgroundColor: "transparent", fontWeight: 500 }
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Form + Report Options */}
          <Grid container spacing={3}>
            {/* Left Side: Generate Report Form */}
            <Grid item xs={12} md={6}>
              <GenerateReportForm
                onSubmit={onSubmit}
                generateReportFormValues={generateReportFormValues}
                stream={stream}
              />
            </Grid>

            {/* Right Side: Report Options */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                style={{
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 24,
                }}
              >
                {/* Report Title */}
                <div>
                  <Typography variant="h6" gutterBottom>
                    Report Options
                  </Typography>

                  <TextField
                    type="text"
                    variant="outlined"
                    label="Report Title"
                    name="reportTitle"
                    value={reportTitle}
                    onChange={(e) => setReportTitle(e.target.value)}
                    fullWidth
                  />
                </div>

                {/* Sort By */}
                <div>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="sort-by-label">Sort By</InputLabel>
                    <Select
                      labelId="sort-by-label"
                      name="sortBy"
                      label="Sort By"
                      value={sortBy}
                      onChange={(e) =>
                        onSortByChange(
                          e.target.value as
                            | "ticketNumber"
                            | "contractorCompanyName"
                            | "shippingCompany"
                        )
                      }
                    >
                      <MenuItem value="ticketNumber">Ticket Number</MenuItem>
                      <MenuItem value="contractorCompanyName">
                        Contractor Company Name
                      </MenuItem>
                      {stream === "hydrovac" && (
                        <MenuItem value="shippingCompany">
                          Shipping Company
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </div>

                {/* Ticket batch (Hydrovac only): report on Standard, B, or both */}
                {stream === "hydrovac" && (
                  <div>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="hydrovac-batch-label">
                        Ticket Batch
                      </InputLabel>
                      <Select
                        labelId="hydrovac-batch-label"
                        name="hydrovacBatch"
                        label="Ticket Batch"
                        value={hydrovacBatch}
                        onChange={(e) =>
                          setHydrovacBatch(
                            e.target.value as "all" | "standard" | "b"
                          )
                        }
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="standard">Regular</MenuItem>
                        <MenuItem value="b">Tractor</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                )}

                {/* Visible Columns */}
                <div>
                  <Typography variant="subtitle1" gutterBottom>
                    Visible Columns
                  </Typography>
                  <Paper
                    style={{ padding: 8, maxHeight: 300, overflowY: "auto" }}
                  >
                    <Grid container spacing={1}>
                      {columns.map((col) => (
                        <Grid item xs={12} sm={6} key={col.id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="primary"
                                checked={visibleColumnIds.includes(
                                  Number(col.id)
                                )}
                                onChange={() =>
                                  onToggleColumnVisible(Number(col.id))
                                }
                              />
                            }
                            label={col.name}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </div>
              </Paper>
            </Grid>
          </Grid>

          {/* Data Table */}
          <Box style={{ marginTop: 32, paddingBottom: 120 }}>
            <PaginatedTableTop>
              <DataTable
                customStyles={tableCustomStyles}
                className={paginationTopClassName}
                fixedHeader
                striped
                columns={visibleColumns}
                data={sortedTickets}
                pagination
                paginationPerPage={50}
                paginationRowsPerPageOptions={[50, 100, 150, 200]}
              />
            </PaginatedTableTop>
          </Box>

          {/* Export Button */}
          <Box
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            bgcolor="white"
            borderTop="1px solid #ddd"
            padding={2}
            zIndex={1201}
          >
            <Container maxWidth="xl">
              <Grid container>
                <Grid item style={{ minWidth: 250 }}>
                  <Button
                    label="Export"
                    color="secondary"
                    variant="contained"
                    type="button"
                    onClick={() => onExport()}
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>
        </Box>
      </TabPanel>

      {/* Templated Reports */}
      <TabPanel value={value} index={TabValues.Template}>
        <TemplateReportForm />
      </TabPanel>

      {/* Royalty: the report and the method behind it */}
      <TabPanel value={value} index={TabValues.Royalty}>
        <RoyaltyReportView />
      </TabPanel>
    </Container>
  );
};

export default connect()(ReportsView);
